import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Palette, Copy } from "lucide-react";
import { toast } from "sonner";

export const ColorPicker = () => {
  const [color, setColor] = useState<{ hex: string; rgb: string; hsl: string } | null>(null);

  const generateColor = () => {
    const array = new Uint8Array(3);
    crypto.getRandomValues(array);
    
    const r = array[0];
    const g = array[1];
    const b = array[2];
    
    const hex = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    const rgb = `rgb(${r}, ${g}, ${b})`;
    
    // Convert to HSL
    const rNorm = r / 255;
    const gNorm = g / 255;
    const bNorm = b / 255;
    
    const max = Math.max(rNorm, gNorm, bNorm);
    const min = Math.min(rNorm, gNorm, bNorm);
    let h = 0, s = 0, l = (max + min) / 2;
    
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      
      switch (max) {
        case rNorm: h = ((gNorm - bNorm) / d + (gNorm < bNorm ? 6 : 0)) / 6; break;
        case gNorm: h = ((bNorm - rNorm) / d + 2) / 6; break;
        case bNorm: h = ((rNorm - gNorm) / d + 4) / 6; break;
      }
    }
    
    const hsl = `hsl(${Math.round(h * 360)}, ${Math.round(s * 100)}%, ${Math.round(l * 100)}%)`;
    
    setColor({ hex, rgb, hsl });
    toast.success("New color generated!");
  };

  const copyToClipboard = (value: string, format: string) => {
    navigator.clipboard.writeText(value);
    toast.success(`${format} copied to clipboard!`);
  };

  return (
    <Card className="p-6 shadow-[var(--shadow-medium)]">
      <div className="flex items-center gap-2 mb-6">
        <Palette className="w-6 h-6 text-primary" />
        <h2 className="text-2xl font-bold text-foreground">Random Color</h2>
      </div>

      <Button onClick={generateColor} className="w-full mb-4">
        Generate Color
      </Button>

      {color && (
        <div className="space-y-4">
          <div 
            className="w-full h-32 rounded-lg shadow-lg transition-all"
            style={{ backgroundColor: color.hex }}
          />
          
          <div className="space-y-2">
            {[
              { label: 'HEX', value: color.hex },
              { label: 'RGB', value: color.rgb },
              { label: 'HSL', value: color.hsl }
            ].map(({ label, value }) => (
              <div key={label} className="flex items-center gap-2 p-3 bg-secondary rounded-lg">
                <span className="text-sm font-medium text-muted-foreground w-12">{label}</span>
                <code className="flex-1 text-sm font-mono text-foreground">{value}</code>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => copyToClipboard(value, label)}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};
